import {
  apiResolverApi,
  bootstrapQueryKeys,
  leadQueryKeys,
  useMutationLeadStatus,
} from "@gymflow/api";
import { LeadStatusDTO, UserStaffBean } from "@gymflow/types";
import {
  infiniteQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import z from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { ModalContext, useClubSettings } from "../../../providers";
import { RouteFeature, RouteLayout } from "../../../routes";
import useGymflowModels from "../../../store";
import { Button, FormSelectInput, FormSelectStaff } from "../../atoms";
import { ConfirmModal } from "../../templates";

const OtherSettingsSchema = z.object({
  trainer: z
    .object({
      id: z.string(),
      label: z.string(),
      value: z.custom<UserStaffBean>(),
    })
    .optional(),
  leadStatus: z.object({
    id: z.number(),
    label: z.string(),
    value: z.custom<LeadStatusDTO>(),
  }),
  homeClub: z.object({
    id: z.number(),
    label: z.string(),
    value: z.number(),
  }),
});

export const MemberOtherSettingsForm = ({ memberId }: { memberId: string }) => {
  const history = useHistory();
  const { createClubLink } = usePortalRoutes();
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { brand } = usePortalRoutes();
  const { club } = useClubSettings();
  const { data: member } = useQuery(
    apiV2.staff.member.queries.idDeprecated(memberId),
  );
  const assignMutation = useMutation(
    apiV2.staff.member.mutations.assignTrainer(),
  );
  const unassignMutation = useMutation(
    apiV2.staff.member.mutations.unassignTrainer(),
  );
  const { changeLeadStatusMutation } = useMutationLeadStatus({ api });
  const { setModal, hide } = useContext(ModalContext);
  const { mutateAsync: updateMemberHomeClub } = useMutation(
    apiV2.staff.member.mutations.updateHomeClub(),
  );
  const formik = useFormik({
    initialValues: {
      trainer: member?.assignedStaffMembers[0]
        ? {
            id: member?.assignedStaffMembers[0].id,
            label: `${member?.assignedStaffMembers[0].firstName} ${member?.assignedStaffMembers[0].lastName}`,
            value: member?.assignedStaffMembers[0],
          }
        : undefined,
      leadStatus: {
        id: member!.lead!.leadStatus.id,
        label: member!.lead!.leadStatus.name,
        value: member!.lead!.leadStatus,
      },
      homeClub: {
        id: member!.homeClubId,
        label: club.name,
        value: club.id,
      },
    },
    onSubmit: async (values) => {
      if (!member) return;
      if (values.trainer?.id) {
        await assignMutation.mutateAsync({
          userId: memberId,
          staffId: values.trainer.value.id,
        });
      } else if (member.assignedStaffMembers[0]?.id) {
        await unassignMutation.mutateAsync({
          userId: memberId,
          staffId: member.assignedStaffMembers[0].id,
        });
      }
      await changeLeadStatusMutation.mutateAsync({
        leadId: member.lead.id,
        newColumn: values.leadStatus.id,
      });
      if (values.homeClub.id !== member.homeClubId) {
        setModal(
          <ConfirmModal
            onHide={hide}
            onCancel={hide}
            onConfirm={async () => {
              await updateMemberHomeClub({
                userId: member.id,
                clubId: values.homeClub.id,
              });
              history.push(
                createClubLink(RouteLayout.Staff, RouteFeature.UserMemberList),
              );
              hide();
              formik.resetForm();
            }}
            cancelText={t("portal.common.cancel")}
            confirmText={t("portal.common.yes")}
            type="warning"
            title={t("pages.member.otherSettings.changeHomeClubTitle")}
          >
            {t("pages.member.otherSettings.changeHomeClubDescription")}
          </ConfirmModal>,
        );
      }
    },
    validationSchema: toFormikValidationSchema(OtherSettingsSchema),
    validate: toFormikValidate(OtherSettingsSchema),
  });
  return (
    <FormikProvider value={formik}>
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-col gap-y-2 p-6">
          <div className="flex flex-col gap-y-1">
            <FormSelectStaff<z.infer<typeof OtherSettingsSchema>>
              name="trainer"
              label={t("pages.member.otherSettings.trainer")}
              withImageByDefault
              containerClassName="flex-1"
              isClearable
            />
          </div>

          <div className="flex flex-col gap-y-1">
            <div className="text-sm font-semibold">
              {t("pages.member.otherSettings.leadStatus")}
            </div>
            <FormSelectInput
              name="leadStatus"
              dataFetchingQuery={() => {
                return infiniteQueryOptions({
                  queryKey: leadQueryKeys.leadStatus({}),
                  queryFn: async () => {
                    return (await api.leadApi.findStatus()).data;
                  },
                  select: (data) => {
                    return {
                      pageParams: data.pageParams,
                      pages: data.pages.map((page) =>
                        page.map((leadStatus) => ({
                          id: leadStatus.id,
                          value: leadStatus.id,
                          label: leadStatus.name,
                        })),
                      ),
                    };
                  },
                  getNextPageParam: () => undefined,
                  initialPageParam: undefined,
                });
              }}
            />
          </div>

          <div className="flex flex-col gap-y-1">
            <div className="text-sm font-semibold">
              {t("pages.member.otherSettings.homeClub")}
            </div>

            <FormSelectInput
              name="homeClub"
              dataFetchingQuery={() => {
                return infiniteQueryOptions({
                  queryKey: bootstrapQueryKeys.listByBrand({ brand }),
                  queryFn: () =>
                    apiResolverApi.findRoutes({
                      page: 0,
                      limit: 50,
                      brand: brand,
                    }),
                  select: (data) => {
                    return {
                      pageParams: data.pageParams,
                      pages: data.pages.map((page) =>
                        page.results.map((route) => ({
                          id: route.clubId,
                          value: route.clubId,
                          label: route.displayName,
                        })),
                      ),
                    };
                  },
                  getNextPageParam: () => undefined,
                  initialPageParam: undefined,
                });
              }}
            />
          </div>
          <div className="text-xs text-gray-600">
            {t("pages.member.otherSettings.homeClubChangeDescription")}
          </div>
        </div>
        <div className="border-b border-gray-200" />
        <div className="flex w-full flex-row justify-end gap-4 p-6">
          <Button
            intent="secondary"
            disabled={!formik.isValid || !formik.dirty}
            onClick={async () => {
              await formik.submitForm();
            }}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
